/**
 * BulkUploadProductRow — renders a single product row inside BulkImageUpload.
 */
import { useRef } from "react";
import { ImageIcon, Check, Loader2, X, Package, AlertCircle } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";

export function BulkUploadProductRow({ product, upload, catName, onFileSelect, onRemove }) {
  const fileInputRef = useRef(null);
  const hasExisting = !!product.image_url;
  const imgSrc = upload?.preview || (product.image_url ? `${API_URL}${product.image_url}` : null);

  return (
    <div
      className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:border-gray-200 transition bg-white"
      data-testid={`bulk-row-${product.id.slice(-6)}`}
    >
      <div className="w-14 h-14 rounded-lg border border-gray-200 overflow-hidden flex-shrink-0 bg-gray-50 relative">
        {imgSrc ? (
          <img src={imgSrc} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-5 h-5 text-gray-300" />
          </div>
        )}
        {upload?.status === "uploading" && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
        )}
        {upload?.status === "saved" && (
          <div className="absolute inset-0 bg-emerald-500/30 flex items-center justify-center">
            <Check className="w-5 h-5 text-white" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
        <p className="text-xs text-gray-400">{catName}</p>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        {upload?.status === "uploaded" && (
          <button
            onClick={() => onRemove(product.id)}
            className="w-7 h-7 rounded-full bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {upload?.status === "saved" ? (
          <span className="text-xs text-emerald-600 font-medium px-2 py-1 bg-emerald-50 rounded-full">Guardado</span>
        ) : upload?.status === "error" ? (
          <span className="text-xs text-red-500 font-medium px-2 py-1 bg-red-50 rounded-full flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Error</span>
        ) : (
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={fileInputRef}
              onChange={e => e.target.files?.[0] && onFileSelect(product.id, e.target.files[0])}
            />
            <span className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${hasExisting ? "bg-gray-100 text-gray-500 hover:bg-gray-200" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"}`}>
              <ImageIcon className="w-3 h-3" />
              {hasExisting ? "Cambiar" : "Subir"}
            </span>
          </label>
        )}
      </div>
    </div>
  );
}
